import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
  RouterProvider,
} from '@tanstack/react-router';
import { Layout, NAV_ITEMS } from '@/components/Layout';
import { LoginPage } from '@/pages/LoginPage';
import { OverviewPage } from '@/pages/OverviewPage';
import { PlaceholderPage } from '@/pages/PlaceholderPage';
import { UsersPage } from '@/pages/UsersPage';
import { UserDetailPage } from '@/pages/UserDetailPage';
import { CasesPage } from '@/pages/CasesPage';
import { CaseDetailPage } from '@/pages/CaseDetailPage';
import { RealtimePage } from '@/pages/RealtimePage';
import { JourneyPage } from '@/pages/JourneyPage';
import { FunilPage } from '@/pages/FunilPage';
import { HarnessPage } from '@/pages/HarnessPage';
import { ColetivaPage } from '@/pages/ColetivaPage';
import { MonetizationPage } from '@/pages/MonetizationPage';
import { ChatPage } from '@/pages/ChatPage';
import { InfraPage } from '@/pages/InfraPage';
import { SecurityPage } from '@/pages/SecurityPage';
import { RescuePage } from '@/pages/RescuePage';
import { SystemPage } from '@/pages/SystemPage';
import { TestingPage } from '@/pages/TestingPage';
import { AICostPage } from '@/pages/AICostPage';
import { ExperimentsPage } from '@/pages/ExperimentsPage';
import { RetencaoPage } from '@/pages/RetencaoPage';
import { AtividadePage } from '@/pages/AtividadePage';
import { SatisfacaoPage } from '@/pages/SatisfacaoPage';
import { ImpugnacoesPage } from '@/pages/ImpugnacoesPage';
import { UnitEconomicsPage } from '@/pages/UnitEconomicsPage';
import { QualidadeVAPage } from '@/pages/QualidadeVAPage';
import { LotesAdminPage } from '@/pages/LotesAdminPage';
import { VAPorVerbaPage } from '@/pages/VAPorVerbaPage';
import { Golden5Page } from '@/pages/Golden5Page';
import { FeedbackPage } from '@/pages/FeedbackPage';
import { BayesPage } from '@/pages/BayesPage';
import { PjcAbPage } from '@/pages/PjcAbPage';
import { MaxAbPage } from '@/pages/MaxAbPage';
import { MaxHealthPage } from '@/pages/MaxHealthPage';
import { FichaAbPage } from '@/pages/FichaAbPage';
import { AcompanhamentoPage } from '@/pages/AcompanhamentoPage';
import { JuizPage } from '@/pages/JuizPage';
import { ConciergePage } from '@/pages/ConciergePage';
import { EdicaoPage } from '@/pages/EdicaoPage';
import MelhoriaPage from '@/pages/MelhoriaPage';
import LoopErroPage from '@/pages/LoopErroPage';
import ReferralsPage from '@/pages/ReferralsPage';
import NewsletterPage from '@/pages/NewsletterPage';
import ProspectorPage from '@/pages/ProspectorPage';
import CreditosPage from '@/pages/CreditosPage';
import TopClientesPage from '@/pages/TopClientesPage';
import { FinanceiroPage } from '@/pages/FinanceiroPage';
import { getAccessToken } from '@/lib/api';
import { AuthProvider } from '@/lib/auth';
import { DateRangeProvider } from '@/lib/dateRange';

const rootRoute = createRootRoute({
  component: () => <Outlet />,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
  beforeLoad: () => {
    if (getAccessToken()) {
      throw redirect({ to: '/' });
    }
  },
});

const appLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  component: () => (
    <Layout>
      <Outlet />
    </Layout>
  ),
  beforeLoad: () => {
    if (!getAccessToken()) {
      throw redirect({ to: '/login' });
    }
  },
});

const overviewRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/',
  component: OverviewPage,
});

// Phase 1 — real pages (Users + Cases). Detail routes are nested
// underneath the list routes via path segments.
const usersListRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/users',
  component: UsersPage,
});

const userDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/users/$userId',
  component: UserDetailPage,
});

const casesListRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/cases',
  component: CasesPage,
});

const caseDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/cases/$caseId',
  component: CaseDetailPage,
});

// Phase 2 — Realtime + Journey + Infra
const realtimeRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/realtime',
  component: RealtimePage,
});

const journeyRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/journey',
  component: JourneyPage,
});

// Funil AARRR (aquisição → ativação → receita → retenção) + insights
const funilRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/funil',
  component: FunilPage,
});

// JuanDz2 (harness JuanDzFix) — placar fase×verba, frentes e vigência de flags (routers/harness.py)
const harnessRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/harness',
  component: HarnessPage,
});

// Ação coletiva — sindicato substituto processual: o que o classificador viu × o que entregamos
const coletivaRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/coletiva',
  component: ColetivaPage,
});

const infraRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/infra',
  component: InfraPage,
});

// Phase 3 — Monetization + Chat
const monetizationRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/monetization',
  component: MonetizationPage,
});

const chatRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/chat',
  component: ChatPage,
});

// Phase 4 — Security
const securityRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/security',
  component: SecurityPage,
});

// Phase 2 — Rescue (rescue_bot dashboard)
const rescueRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/rescue',
  component: RescuePage,
});

// Phase 2/3 — System ops + flags
const systemRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/system',
  component: SystemPage,
});

// Phase 3 — Testing (eval runs)
const testingRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/testing',
  component: TestingPage,
});

// Phase 3 — AI Cost
const aiCostRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/ai-cost',
  component: AICostPage,
});

// Phase 4 — Experiments (A/B variants)
const experimentsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/experiments',
  component: ExperimentsPage,
});

// Phase 4 — Retenção (ativação + retenção)
const retencaoRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/retencao',
  component: RetencaoPage,
});

// Atividade — comportamento de usuários (DAU/WAU/MAU, pirâmide, cohorts…)
const atividadeRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/atividade',
  component: AtividadePage,
});

// Impugnações — monitor do beta do subproduto Impugna (régua de graduação,
// funil, detectores, adoção, qualidade/feedback, saúde)
const impugnacoesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/impugnacoes',
  component: ImpugnacoesPage,
});

const feedbackRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/feedback',
  component: FeedbackPage,
});

// Unit Economics — CAC/LTV/payback/ROAS por canal (routers/unit_economics.py)
const unitEconomicsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/unit-economics',
  component: UnitEconomicsPage,
});

// Qualidade (VA vs perito) — tendência do juiz-régua (routers/qualidade_va.py)
const qualidadeVARoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/qualidade-va',
  component: QualidadeVAPage,
});

// Lotes (Cálculos em Massa) — saúde da função de lote (routers/lotes.py)
const lotesAdminRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/lotes',
  component: LotesAdminPage,
});

// VA por verba — perfil de defeito POR VERBA do juiz-régua (routers/va_por_verba.py)
const vaPorVerbaRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/va-por-verba',
  component: VAPorVerbaPage,
});

// Golden5 vs Perito — placar da régua determinística full-pipeline (routers/golden5.py)
const golden5Route = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/golden5',
  component: Golden5Page,
});

// Satisfação — índice diário de satisfação (0-100), 4 pilares, detalhe
// exaustivo por dia (routers/satisfacao.py::indice_satisfacao)
const satisfacaoRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/satisfacao',
  component: SatisfacaoPage,
});

const bayesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/bayes',
  component: BayesPage,
});

// [PJC_COBRADO 21/09] A/B .pjc cobrado à parte (routers/pjc_ab.py)
const pjcAbRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/pjc-ab',
  component: PjcAbPage,
});

// [MAX_AB 11/10] A/B da opção premium Max, 3 créditos (routers/max_ab.py)
const maxAbRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/max-ab',
  component: MaxAbPage,
});

const maxHealthRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/max-health',
  component: MaxHealthPage,
});

// [FICHA A/B 28/09] A/B da ficha de liquidação (routers/ficha_ab.py)
const fichaAbRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/ficha-ab',
  component: FichaAbPage,
});

// [CNJ-WATCH 21/09] acompanhamento de processo (routers/acompanhamento.py)
const acompanhamentoRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/acompanhamento',
  component: AcompanhamentoPage,
});

const juizRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/juiz',
  component: JuizPage,
});

const conciergeRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/concierge',
  component: ConciergePage,
});

const edicaoRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/edicao',
  component: EdicaoPage,
});

const melhoriaRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/melhoria',
  component: MelhoriaPage,
});

const loopErroRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/loop-erro',
  component: LoopErroPage,
});

const referralsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/referrals',
  component: ReferralsPage,
});

// Fase 4 (Blog/Newsletter) — KPIs de edição + posts das personas
const newsletterRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/newsletter',
  component: NewsletterPage,
});

// Prospector — cold outbound (Instantly): campanhas, funil por lead, conversas
const prospectorRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/prospector',
  component: ProspectorPage,
});

// Créditos & Receita — economia de créditos (compras Stripe × consumo × grants)
const creditosRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/creditos',
  component: CreditosPage,
});

// 30 Clientes Top — maiores clientes da história por receita Stripe
const topClientesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/top-clientes',
  component: TopClientesPage,
});

const financeiroRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/financeiro',
  component: FinanceiroPage,
});

// Rotas implementadas do layout — fonte única. O conjunto de paths implementados é
// DERIVADO desta lista (nunca mantido à mão): 30/09 a aba /coletiva entrou aqui e no
// NAV_ITEMS mas ficou fora do Set manual → rota placeholder duplicada → invariant do
// TanStack Router no boot → admin inteiro em tela branca.
const appRoutes = [
  overviewRoute,
  financeiroRoute,
  usersListRoute,
  userDetailRoute,
  casesListRoute,
  caseDetailRoute,
  realtimeRoute,
  journeyRoute,
  funilRoute,
  harnessRoute,
  coletivaRoute,
  infraRoute,
  monetizationRoute,
  chatRoute,
  securityRoute,
  rescueRoute,
  systemRoute,
  testingRoute,
  aiCostRoute,
  experimentsRoute,
  retencaoRoute,
  atividadeRoute,
  satisfacaoRoute,
  impugnacoesRoute,
  unitEconomicsRoute,
  qualidadeVARoute,
  vaPorVerbaRoute,
  golden5Route,
  lotesAdminRoute,
  feedbackRoute,
  bayesRoute,
  pjcAbRoute,
  maxAbRoute,
  maxHealthRoute,
  fichaAbRoute,
  acompanhamentoRoute,
  juizRoute,
  conciergeRoute,
  edicaoRoute,
  melhoriaRoute,
  loopErroRoute,
  referralsRoute,
  newsletterRoute,
  prospectorRoute,
  creditosRoute,
  topClientesRoute,
] as const;
const IMPLEMENTED_PATHS = new Set<string>(
  // options.path (o que passamos ao createRoute), não r.path: este só existe após o init() do router.
  appRoutes
    .map((r) => (r.options as { path?: string }).path)
    .filter((p): p is string => !!p),
);
// Placeholders para entradas do nav ainda sem página.
const placeholderRoutes = NAV_ITEMS.filter(
  (i) => !IMPLEMENTED_PATHS.has(i.to),
).map((item) =>
  createRoute({
    getParentRoute: () => appLayoutRoute,
    path: item.to,
    component: PlaceholderPage,
  }),
);

const routeTree = rootRoute.addChildren([
  loginRoute,
  appLayoutRoute.addChildren([...appRoutes, ...placeholderRoutes]),
]);

const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

export default function App() {
  return (
    <AuthProvider>
      <DateRangeProvider>
        <RouterProvider router={router} />
      </DateRangeProvider>
    </AuthProvider>
  );
}
